import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, User, Mail, Globe, MapPin, Building2, FolderKanban, Phone, Upload, PlusCircle, MinusCircle, Link2 } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/context/WorkspaceContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PhoneInputField } from '@/components/ui/phone-input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { uploadToR2 } from '@/lib/r2-storage';

interface ClientDetailProps {
  id: string;
  onBack: () => void;
}

export const ClientDetail: React.FC<ClientDetailProps> = ({ id, onBack }) => {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit Form Fields
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editSource, setEditSource] = useState('');
  
  const [editEmail, setEditEmail] = useState('');
  const [editEmailType, setEditEmailType] = useState('work');
  const [editAdditionalEmails, setEditAdditionalEmails] = useState<{email: string, type: string}[]>([]);
  
  const [editPhone, setEditPhone] = useState('');
  const [editPhoneType, setEditPhoneType] = useState('work');
  const [editAdditionalPhones, setEditAdditionalPhones] = useState<{phone: string, type: string}[]>([]);
  
  const [editSocialLinks, setEditSocialLinks] = useState<{platform: string, url: string}[]>([]);

  const [editWebsite, setEditWebsite] = useState('');
  const [editDesignation, setEditDesignation] = useState('');

  const [editCountry, setEditCountry] = useState('');
  const [editState, setEditState] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editZip, setEditZip] = useState('');
  const [editStreetAddress, setEditStreetAddress] = useState('');
  const [editAvatarUrl, setEditAvatarUrl] = useState('');
  const [editCompanyId, setEditCompanyId] = useState('none');
  const [editCompanyMode, setEditCompanyMode] = useState('existing');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editCompanyCategory, setEditCompanyCategory] = useState('');
  const [editSendEmail, setEditSendEmail] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (id && currentWorkspace) {
      loadClientData();
    }
  }, [id, currentWorkspace]);

  const loadClientData = async () => {
    if (!currentWorkspace) return;
    try {
      setLoading(true);
      
      // Fetch member data
      const { data: memberData, error: memberError } = await supabase
        .from('workspace_members')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .eq('user_id', id)
        .single();
        
      if (memberError && memberError.code !== 'PGRST116') throw memberError;

      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();
        
      if (profileError && profileError.code !== 'PGRST116') throw profileError;

      let clientObj: any = null;
      if (memberData || profileData) {
        const mergedFirstName = memberData?.first_name || profileData?.first_name || '';
        const mergedLastName = memberData?.last_name || profileData?.last_name || '';
        clientObj = {
          ...profileData,
          ...memberData,
          name: profileData || memberData ? `${mergedFirstName} ${mergedLastName}`.trim() || profileData?.email || memberData?.email : memberData?.email
        };
      } else {
        // Try fetching from invitations
        const { data: invData } = await supabase
          .from('workspace_invitations')
          .select('*')
          .eq('workspace_id', currentWorkspace.id)
          .eq('id', id)
          .maybeSingle();
        
        if (invData) {
          clientObj = {
            ...invData,
            status: 'pending',
            isInvitation: true,
            name: `${invData.first_name || ''} ${invData.last_name || ''}`.trim() || invData.email
          };
        }
      }
      setClient(clientObj);

      // Fetch linked companies
      const { data: companyLinks } = await supabase
        .from('workspace_company_clients')
        .select('company_id')
        .eq('user_id', id);
        
      if (companyLinks && companyLinks.length > 0) {
        const companyIds = companyLinks.map(l => l.company_id);
        const { data: companyData } = await supabase
          .from('workspace_companies')
          .select('id, name, logo_url, category')
          .in('id', companyIds)
          .eq('workspace_id', currentWorkspace.id);
          
        if (companyData) setCompanies(companyData);
      } else {
        setCompanies([]);
      }

      // Fetch assigned projects
      const { data: projectLinks } = await supabase
        .from('project_members')
        .select('project_id')
        .eq('user_id', id);
        
      if (projectLinks && projectLinks.length > 0) {
        const projectIds = projectLinks.map(l => l.project_id);
        const { data: projectData } = await supabase
          .from('projects')
          .select('id, name, status, slug')
          .in('id', projectIds)
          .eq('workspace_id', currentWorkspace.id);
          
        if (projectData) setProjects(projectData);
      } else {
        setProjects([]);
      }

      // Fetch all workspace companies for selection
      const { data: allCompData } = await supabase
        .from('workspace_companies')
        .select('id, name')
        .eq('workspace_id', currentWorkspace.id);
        
      if (allCompData) setAllCompanies(allCompData);

    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingAvatar(true);
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}.${ext}`;
      const { publicUrl } = await uploadToR2({
        fileName,
        file,
        folder: 'client-avatars'
      });
      setEditAvatarUrl(publicUrl);
      toast.success('Profile image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleOpenEdit = () => {
    if (!client) return;
    
    // Populate form fields
    const first_name = client.first_name || client.name?.split(' ')[0] || '';
    const last_name = client.last_name || client.name?.split(' ').slice(1).join(' ') || '';
    setEditFirstName(first_name);
    setEditLastName(last_name);
    setEditSource(client.source || '');
    
    setEditEmail(client.email || '');
    setEditEmailType(client.email_type || 'work');
    setEditAdditionalEmails(client.additional_emails || []);
    
    setEditPhone(client.phone || '');
    setEditPhoneType(client.phone_type || 'work');
    setEditAdditionalPhones(client.additional_phones || []);
    
    if (client.social_media && typeof client.social_media === 'object') {
       const links = Object.entries(client.social_media).map(([platform, url]) => ({ platform, url: url as string }));
       setEditSocialLinks(links.length > 0 ? links : [{platform: 'linkedin', url: ''}]);
    } else {
       setEditSocialLinks([{platform: 'linkedin', url: ''}]);
    }
    
    setEditWebsite(client.website || '');
    setEditDesignation(client.designation || '');

    setEditCountry(client.country || '');
    setEditState(client.state || '');
    setEditCity(client.city || '');
    setEditZip(client.zip || '');
    setEditStreetAddress(client.street_address || '');
    setEditAvatarUrl(client.avatar_url || '');
    
    // Set editCompanyId based on linked company if any
    setEditCompanyId(companies[0]?.id || client.company_id || 'none');
    
    setEditOpen(true);
  };

  const handleUpdateClient = async () => {
    if (!currentWorkspace) return;
    try {
      setSaving(true);
      
      const isInvitation = client.isInvitation;
      const finalCountry = editCountry.trim();
      const finalState = editState.trim();
      const finalCompanyName = editCompanyId === 'none' ? '' : allCompanies.find(c => c.id === editCompanyId)?.name || '';

      const socialMediaObj: Record<string, string> = {};
      editSocialLinks.forEach(link => {
        if (link.url.trim() && link.platform) {
          socialMediaObj[link.platform] = link.url.trim();
        }
      });

      const updatePayload = {
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        source: editSource.trim(),
        email_type: editEmailType,
        additional_emails: editAdditionalEmails.length > 0 ? editAdditionalEmails : null,
        phone: editPhone.trim(),
        phone_type: editPhoneType,
        additional_phones: editAdditionalPhones.length > 0 ? editAdditionalPhones : null,
        social_media: Object.keys(socialMediaObj).length > 0 ? socialMediaObj : null,
        website: editWebsite.trim(),
        designation: editDesignation.trim(),
        company_name: finalCompanyName,
        country: finalCountry,
        state: finalState,
        city: editCity.trim(),
        zip: editZip.trim(),
        street_address: editStreetAddress.trim(),
        avatar_url: editAvatarUrl
      };

      if (isInvitation) {
        // Update workspace_invitations
        const { error } = await supabase
          .from('workspace_invitations')
          .update({
            ...updatePayload,
            company_id: editCompanyId === 'none' ? null : editCompanyId
          })
          .eq('id', client.id);
          
        if (error) throw error;
      } else {
        const userId = client.user_id || client.id;
        
        // 1. Update profiles
        const { error: profileErr } = await supabase
          .from('profiles')
          .update(updatePayload)
          .eq('id', userId);
          
        if (profileErr) throw profileErr;
        
        // 2. Update workspace_members
        const { error: memberErr } = await supabase
          .from('workspace_members')
          .update({
            website: editWebsite.trim(),
            company_name: finalCompanyName,
            country: finalCountry,
            state: finalState,
            city: editCity.trim(),
            zip: editZip.trim(),
            street_address: editStreetAddress.trim(),
            company_id: editCompanyId === 'none' ? null : editCompanyId,
            first_name: editFirstName.trim(),
            last_name: editLastName.trim()
          })
          .eq('workspace_id', currentWorkspace.id)
          .eq('user_id', userId);
          
        if (memberErr) throw memberErr;
        
        // 3. Update junction table for company linking
        await supabase
          .from('workspace_company_clients')
          .delete()
          .eq('user_id', userId);
          
        if (editCompanyId !== 'none') {
          await supabase
            .from('workspace_company_clients')
            .insert({
              company_id: editCompanyId,
              user_id: userId
            });
        }
      }
      
      toast.success('Client updated successfully');
      setEditOpen(false);
      await loadClientData();
    } catch (error: any) {
      console.error('Error updating client:', error);
      toast.error(error.message || 'Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <User className="w-12 h-12 text-muted-foreground opacity-50" />
        <h2 className="text-xl font-semibold">Client not found</h2>
        <Button variant="outline" onClick={onBack}>
          Back to Clients
        </Button>
      </div>
    );
  }

  const formatAddress = () => {
    const parts = [client.street_address, client.city, client.state, client.zip, client.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const address = formatAddress();

  return (
    <div className="w-full">
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Clients
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info Column */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 text-left">
            <div className="flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-workspace-accent/15 flex items-center justify-center text-3xl font-semibold text-workspace-accent overflow-hidden shrink-0 mb-4">
                {client.avatar_url ? (
                  <img src={client.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  (client.name?.[0] || 'C').toUpperCase()
                )}
              </div>
              <h1 className="text-xl font-bold text-foreground mb-1">{client.name}</h1>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mb-4 ${
                client.status === 'active' 
                  ? 'bg-emerald-500/10 text-emerald-500' 
                  : 'bg-amber-500/10 text-amber-500'
              }`}>
                {client.status ? client.status.charAt(0).toUpperCase() + client.status.slice(1) : 'Unknown'}
              </span>
            </div>

            <div className="space-y-4 pt-6 border-t border-border">
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</div>
                  <div className="text-sm text-foreground break-all">{client.email || '—'}</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Phone</div>
                  <div className="text-sm text-foreground">{client.phone || '—'}</div>
                </div>
              </div>



            </div>

            <Button 
              variant="outline" 
              className="w-full mt-6 border-border hover:bg-muted text-foreground"
              onClick={handleOpenEdit}
            >
              Edit Details
            </Button>
          </div>
        </div>

        {/* Details Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Linked Companies */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Building2 className="w-5 h-5 text-workspace-accent" />
                Linked Companies
              </h2>
              <span className="text-sm text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full font-medium">
                {companies.length}
              </span>
            </div>

            <div className="flex flex-col gap-4 mb-6 pb-6 border-b border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Company Link</div>
                    <div className="text-sm text-foreground">{client.company_name || '—'}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FolderKanban className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category / Industry</div>
                    <div className="text-sm text-foreground">{client.category || '—'}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Website</div>
                    {client.website ? (
                      <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-workspace-accent hover:underline break-all">
                        {client.website}
                      </a>
                    ) : (
                      <div className="text-sm text-muted-foreground">—</div>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Designation</div>
                    <div className="text-sm text-foreground">{client.designation || '—'}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 mt-2">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Address</div>
                  <div className="text-sm text-foreground break-words">{address || '—'}</div>
                </div>
              </div>
            </div>

            {companies.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {companies.map(company => (
                  <div 
                    key={company.id} 
                    className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:border-workspace-accent/50 cursor-pointer transition-colors group/company"
                    onClick={() => setSearchParams({ tab: 'companies', companyId: company.id })}
                  >
                    <div className="w-10 h-10 rounded-lg bg-workspace-accent/10 flex items-center justify-center shrink-0 overflow-hidden">
                      {company.logo_url ? (
                        <img src={company.logo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="w-5 h-5 text-workspace-accent" />
                      )}
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-foreground group-hover/company:text-workspace-accent transition-colors">{company.name}</div>
                      {company.category && (
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">{company.category}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-border rounded-lg bg-muted/20">
                <Building2 className="w-8 h-8 text-muted-foreground mx-auto mb-3 opacity-50" />
                <h3 className="text-base font-medium text-foreground mb-1">No linked companies</h3>
                <p className="text-sm text-muted-foreground">This client is not linked to any companies.</p>
              </div>
            )}
          </div>

          {/* Assigned Projects */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-workspace-accent" />
                Assigned Projects
              </h2>
              <span className="text-sm text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full font-medium">
                {projects.length}
              </span>
            </div>

            {projects.length > 0 ? (
              <div className="space-y-3">
                {projects.map(project => (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-workspace-accent/30 transition-colors cursor-pointer group/project"
                    onClick={() => navigate(`/boards/projects/${project.slug}`)}
                  >
                    <div className="flex items-center gap-3">
                      <FolderKanban className="w-5 h-5 text-muted-foreground group-hover/project:text-workspace-accent transition-colors" />
                      <span className="font-medium text-foreground group-hover/project:text-workspace-accent transition-colors">{project.name}</span>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                      {project.status || 'Active'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-border rounded-lg bg-muted/20">
                <FolderKanban className="w-8 h-8 text-muted-foreground mx-auto mb-3 opacity-50" />
                <h3 className="text-base font-medium text-foreground mb-1">No assigned projects</h3>
                <p className="text-sm text-muted-foreground">This client hasn't been assigned to any projects yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Client Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-4xl bg-card border-border p-0 overflow-hidden text-left">
          <DialogDescription className="sr-only">Edit client profile and address details</DialogDescription>
          <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
            <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
              <User className="w-5 h-5 text-workspace-accent" /> Edit Client Details
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[80vh]">
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Main Details */}
                <div className="space-y-6">
                  {/* Profile Image & Basic Information Section */}
                  <Accordion type="multiple" defaultValue={["general"]} className="w-full">
                    <AccordionItem value="general" className="border-border">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4 pt-0">
                        General
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pb-4">
                    {/* Top: Profile Image */}
                    <div className="space-y-2">
                      <Label className="text-foreground block text-center sm:text-left">Profile Image</Label>
                      <div className="flex justify-center sm:justify-start">
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="relative group cursor-pointer w-36"
                        >
                          <div className={`
                            w-full aspect-square rounded-xl border-2 border-dashed 
                            flex flex-col items-center justify-center gap-1 transition-all p-3 text-center
                            ${editAvatarUrl ? 'border-workspace-accent bg-workspace-accent/5' : 'border-border hover:border-workspace-accent/50 hover:bg-muted/50'}
                          `}>
                            {uploadingAvatar ? (
                              <LoadingSpinner className="h-6 w-6" />
                            ) : editAvatarUrl ? (
                              <div className="flex flex-col items-center gap-1 w-full h-full justify-center">
                                <div className="w-16 h-16 rounded-full border-2 border-workspace-accent overflow-hidden shadow-lg">
                                  <img src={editAvatarUrl} alt="Preview" className="w-full h-full object-cover" />
                                </div>
                                <span className="text-[10px] font-medium text-workspace-accent">Click to change</span>
                              </div>
                            ) : (
                              <>
                                <div className="p-2 rounded-full bg-workspace-accent/10 text-workspace-accent group-hover:scale-110 transition-transform">
                                  <Upload className="w-5 h-5" />
                                </div>
                                <div className="text-center">
                                  <p className="text-xs font-semibold text-workspace-accent">Upload Photo</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">PNG, JPG (5MB)</p>
                                </div>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleAvatarUpload}
                            accept="image/*"
                            className="hidden"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Basic Information */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-foreground flex items-center gap-1.5">First Name <span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="John"
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          className="bg-background border-input text-foreground h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground flex items-center gap-1.5">Last Name <span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="Doe"
                          value={editLastName}
                          onChange={(e) => setEditLastName(e.target.value)}
                          className="bg-background border-input text-foreground h-11"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-foreground flex items-center gap-1.5">Client Source</Label>
                      <Input value={editSource} onChange={e => setEditSource(e.target.value)} placeholder="e.g. Website, Referral" className="bg-background border-input text-foreground h-11" />
                    </div>
                      </AccordionContent>
                    </AccordionItem>
                  
                    <AccordionItem value="contact-info" className="border-border">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                        Contact Information
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pb-4">
                        <div className="space-y-4">
                          <div className="flex gap-2 items-center">
                            <Input className="flex-1 h-11 bg-background border-input text-foreground" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="Default Email *" required />
                            <div className="w-[100px]">
                              <Select value={editEmailType} onValueChange={(val: any) => setEditEmailType(val)}>
                                <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="work">Work</SelectItem>
                                  <SelectItem value="personal">Personal</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {editAdditionalEmails.map((ae, idx) => (
                            <div key={`email-${idx}`} className="flex gap-2 items-center">
                              <Input className="flex-1 h-11 bg-background border-input text-foreground" type="email" value={ae.email} onChange={e => {
                                const newEmails = [...editAdditionalEmails];
                                newEmails[idx].email = e.target.value;
                                setEditAdditionalEmails(newEmails);
                              }} placeholder="Additional Email" />
                              <div className="w-[100px]">
                                <Select value={ae.type} onValueChange={(val: any) => {
                                  const newEmails = [...editAdditionalEmails];
                                  newEmails[idx].type = val;
                                  setEditAdditionalEmails(newEmails);
                                }}>
                                  <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                  <SelectContent className="bg-popover border-border">
                                    <SelectItem value="work">Work</SelectItem>
                                    <SelectItem value="personal">Personal</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <button type="button" onClick={() => setEditAdditionalEmails(editAdditionalEmails.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                                <MinusCircle className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          
                          <button type="button" onClick={() => setEditAdditionalEmails([...editAdditionalEmails, {email: '', type: 'personal'}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                            <PlusCircle className="w-3 h-3" /> Add Email
                          </button>
                        </div>
                        
                        <div className="space-y-4 pt-2">
                          <div className="flex gap-2 items-center">
                            <PhoneInputField value={editPhone} onChange={setEditPhone} placeholder="Default Phone" />
                            <div className="w-[100px]">
                              <Select value={editPhoneType} onValueChange={(val: any) => setEditPhoneType(val)}>
                                <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="work">Work</SelectItem>
                                  <SelectItem value="personal">Personal</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {editAdditionalPhones.map((ap, idx) => (
                            <div key={`phone-${idx}`} className="flex gap-2 items-center">
                              <PhoneInputField value={ap.phone} onChange={e => {
                                const newPhones = [...editAdditionalPhones];
                                newPhones[idx].phone = e;
                                setEditAdditionalPhones(newPhones);
                              }} placeholder="Additional Phone" />
                              <div className="w-[100px]">
                                <Select value={ap.type} onValueChange={(val: any) => {
                                  const newPhones = [...editAdditionalPhones];
                                  newPhones[idx].type = val;
                                  setEditAdditionalPhones(newPhones);
                                }}>
                                  <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                  <SelectContent className="bg-popover border-border">
                                    <SelectItem value="work">Work</SelectItem>
                                    <SelectItem value="personal">Personal</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <button type="button" onClick={() => setEditAdditionalPhones(editAdditionalPhones.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                                <MinusCircle className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          
                          <button type="button" onClick={() => setEditAdditionalPhones([...editAdditionalPhones, {phone: '', type: 'personal'}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                            <PlusCircle className="w-3 h-3" /> Add Phone
                          </button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="social-profiles" className="border-border border-b-0">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                        Social Profiles
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        {editSocialLinks.map((sl, idx) => (
                          <div key={`social-${idx}`} className="flex gap-2 items-center">
                            <div className="w-[130px]">
                              <Select value={sl.platform} onValueChange={(val: any) => {
                                const newLinks = [...editSocialLinks];
                                newLinks[idx].platform = val;
                                setEditSocialLinks(newLinks);
                              }}>
                                <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                                  <SelectItem value="twitter">X / Twitter</SelectItem>
                                  <SelectItem value="facebook">Facebook</SelectItem>
                                  <SelectItem value="instagram">Instagram</SelectItem>
                                  <SelectItem value="youtube">YouTube</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex-1 relative">
                              <Link2 className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                              <Input className="w-full h-11 bg-background border-input text-foreground pl-10" value={sl.url} onChange={e => {
                                const newLinks = [...editSocialLinks];
                                newLinks[idx].url = e.target.value;
                                setEditSocialLinks(newLinks);
                              }} placeholder="Profile URL" />
                            </div>
                            <button type="button" onClick={() => setEditSocialLinks(editSocialLinks.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                              <MinusCircle className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        
                        <button type="button" onClick={() => setEditSocialLinks([...editSocialLinks, {platform: 'linkedin', url: ''}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                          <PlusCircle className="w-3 h-3" /> Add Social Link
                        </button>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Right Column - Company Details & Settings */}
                <div className="space-y-6 flex flex-col h-full">
                  <Accordion type="multiple" defaultValue={["company-details"]} className="w-full">
                    <AccordionItem value="company-details" className="border-border">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4 pt-0">
                        Company Details
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pb-4">
                        <Tabs value={editCompanyMode} onValueChange={(v: any) => setEditCompanyMode(v)} className="w-full">
                          <TabsList className="grid w-full grid-cols-2 mb-4 bg-muted/50">
                            <TabsTrigger value="existing">Link Company</TabsTrigger>
                            <TabsTrigger value="new">Edit Company</TabsTrigger>
                          </TabsList>

                          <TabsContent value="existing" className="space-y-4 mt-0">
                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Select Company</Label>
                              <Select value={editCompanyId} onValueChange={(val) => setEditCompanyId(val)}>
                                <SelectTrigger className="bg-background border-input text-foreground h-11">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-muted-foreground" />
                                    <SelectValue placeholder="Select a Company" />
                                  </div>
                                </SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="none">No Company</SelectItem>
                                  {allCompanies.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-[11px] text-muted-foreground pl-1 italic">
                                Select an existing company to link this client.
                              </p>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Designation</Label>
                              <Input value={editDesignation} onChange={e => setEditDesignation(e.target.value)} className="bg-background border-input text-foreground h-11" placeholder="e.g. Certified Life Coach" />
                            </div>
                          </TabsContent>

                          <TabsContent value="new" className="space-y-6 mt-0">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-foreground">Company Name</Label>
                                <Input placeholder="e.g. Acme Corp" value={editCompanyName} onChange={e => setEditCompanyName(e.target.value)} className="bg-background border-input text-foreground h-11" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-foreground">Category / Industry</Label>
                                <Input placeholder="e.g. Oil & Energy" value={editCompanyCategory} onChange={e => setEditCompanyCategory(e.target.value)} className="bg-background border-input text-foreground h-11" />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Website</Label>
                              <div className="relative">
                                <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                  placeholder="https://example.com"
                                  value={editWebsite}
                                  onChange={(e) => setEditWebsite(e.target.value)}
                                  className="bg-background border-input text-foreground h-11 pl-10"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Designation</Label>
                              <Input value={editDesignation} onChange={e => setEditDesignation(e.target.value)} className="bg-background border-input text-foreground h-11" placeholder="e.g. Certified Life Coach" />
                            </div>
                          </TabsContent>
                        </Tabs>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="address" className="border-border border-b-0">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                        Address
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Street Address</Label>
                          <Input placeholder="123 Main St" value={editStreetAddress} onChange={e => setEditStreetAddress(e.target.value)} className="bg-background border-input text-foreground h-11" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-foreground">City</Label>
                            <Input placeholder="New York" value={editCity} onChange={e => setEditCity(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">State / Province</Label>
                            <Input placeholder="NY" value={editState} onChange={e => setEditState(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-foreground">ZIP / Postal Code</Label>
                            <Input placeholder="10001" value={editZip} onChange={e => setEditZip(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">Country</Label>
                            <Input placeholder="United States" value={editCountry} onChange={e => setEditCountry(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Notification Control */}
                  <div className="flex items-center space-x-3 p-4 rounded-xl bg-workspace-accent/5 mt-auto">
                    <Checkbox
                      id="editSendEmail"
                      checked={editSendEmail}
                      onCheckedChange={(checked) => setEditSendEmail(!!checked)}
                      className="border-workspace-accent data-[state=checked]:bg-workspace-accent w-5 h-5"
                    />
                    <div className="space-y-0.5">
                      <label
                        htmlFor="editSendEmail"
                        className="text-sm font-semibold text-foreground cursor-pointer"
                      >
                        Send magic link invitation now
                      </label>
                      <p className="text-xs text-muted-foreground">Client will receive a passwordless login link via email.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/20 gap-3">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="h-11 px-6">Cancel</Button>
            <Button
              onClick={handleUpdateClient}
              disabled={saving || !editFirstName.trim() || !editLastName.trim()}
              className="bg-workspace-accent text-white hover:bg-workspace-accent-hover h-11 px-8 shadow-md hover:shadow-lg transition-all"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
